import React from 'react'
import { connect, ConnectedProps } from 'react-redux'

import {
  NavButton,
  PaginationControls,
  PaginationInfo,
} from 'controlled-components'
import {
  selectTopPage,
  selectTopPageSize,
  selectTopTotalCount,
  setTopPage,
} from 'slices/aiSegmentBuilder/picker'

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

interface OwnProps {
  pickerId: number
  goToPage?: (page: number) => void
}

type Props = OwnProps & ConnectedProps<typeof connector>

// ═══════════════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Container for pagination controls in the top selector header.
 * Connects to Redux and composes atomic pagination components.
 */
const PaginationContainer: React.FC<Props> = ({
  pickerId,
  goToPage,
  page,
  totalCount,
  pageSize,
  setPage,
}) => {
  // Derived: pagination values
  const startItem = totalCount > 0 ? (page - 1) * pageSize + 1 : 0
  const endItem = Math.min(page * pageSize, totalCount)
  const totalPages = Math.ceil(totalCount / pageSize)
  const hasPrevPage = page > 1
  const hasNextPage = page < totalPages

  // Handlers
  const handlePrevPage = () => {
    const newPage = page - 1
    setPage({ pickerId, page: newPage })
    goToPage?.(newPage)
  }

  const handleNextPage = () => {
    const newPage = page + 1
    setPage({ pickerId, page: newPage })
    goToPage?.(newPage)
  }

  // Format display text
  const displayText =
    totalCount > 0 ? `${startItem} - ${endItem} of ${totalCount}` : '0 of 0'

  return (
    <>
      <PaginationInfo>{displayText}</PaginationInfo>
      <PaginationControls>
        <NavButton
          direction="prev"
          onClick={handlePrevPage}
          disabled={!hasPrevPage}
        />
        <NavButton
          direction="next"
          onClick={handleNextPage}
          disabled={!hasNextPage}
        />
      </PaginationControls>
    </>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// Redux Connection
// ═══════════════════════════════════════════════════════════════════════════

const mapStateToProps = (state: any, ownProps: OwnProps) => ({
  page: selectTopPage(state, ownProps.pickerId),
  totalCount: selectTopTotalCount(state, ownProps.pickerId),
  pageSize: selectTopPageSize(state, ownProps.pickerId),
})

const mapDispatchToProps = {
  setPage: setTopPage,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

export default connector(PaginationContainer)
